/**
 * N1 · The shared shell for every details step.
 *
 * One implementation of: header + single back affordance, one H1, the named
 * "what's still missing" block, and ONE pinned primary CTA with an optional
 * blue sentence link BELOW it (G3/N4 — never a second button beside it).
 *
 * Splitting the details form into single-purpose screens must not create a
 * second implementation of the form chrome, so screens supply only their fields.
 */
import { FormEvent, ReactNode } from "react";
import {
  Display,
  FunnelContainer,
  FunnelHeader,
  FunnelPage,
  PrimaryButton,
  SentenceLink,
  StickyBar,
} from "@/components/consumer/chrome";

export default function DetailsStepPage({
  testId,
  backTo,
  backLabel,
  step,
  title,
  children,
  missing,
  continueLabel,
  onContinue,
  secondary,
}: {
  testId: string;
  backTo: string;
  backLabel: string;
  step?: ReactNode;
  title: string;
  children: ReactNode;
  missing: string[];
  continueLabel: string;
  onContinue: () => void;
  secondary?: { label: string; onClick: () => void; testId?: string };
}) {
  const canSubmit = missing.length === 0;

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (canSubmit) onContinue();
  }

  return (
    <FunnelPage testId={testId}>
      <FunnelContainer size="form">
        <FunnelHeader backTo={backTo} backLabel={backLabel} step={step} />

        <Display className="mb-[22px] max-w-[20ch]">{title}</Display>

        <form onSubmit={submit} className="space-y-0">
          {children}

          {!canSubmit && (
            <div
              className="mt-[26px] rounded-[18px] border border-[#C41112]/25 bg-[#C41112]/[0.06] p-[18px]"
              role="status"
              aria-live="polite"
              data-testid={`${testId}-missing`}
            >
              <ul className="m-0 list-disc space-y-1 pl-5">
                {missing.map((label) => (
                  <li key={label} className="text-[14px] leading-[1.5] text-[#C41112]">
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      </FunnelContainer>

      <StickyBar>
        <PrimaryButton
          onClick={() => submit()}
          disabled={!canSubmit}
          data-testid={`${testId}-continue`}
          className="basis-full"
        >
          {continueLabel}
        </PrimaryButton>
        {secondary ? (
          <span className="basis-full text-center">
            <SentenceLink onClick={secondary.onClick} data-testid={secondary.testId}>
              {secondary.label}
            </SentenceLink>
          </span>
        ) : null}
      </StickyBar>
    </FunnelPage>
  );
}
